import { useCallback, useEffect, useRef, useState } from 'react'
import ReactNativeBlobUtil from 'react-native-blob-util'
import { useDispatchAsync } from '@src/utils'
import { ReactNativeFile } from '@src/utils/image'
import { ensureMicrophonePermission } from '@src/utils/microphonePermission'
import { TranscribeUtteranceMutation } from '../graphql/transcribeUtterance.generated'
import { NoraAudioCapture, NoraUtterance } from '../native/NoraAudioCapture'
import type { UseNoraTranscriptionOptions } from './useNoraTranscription.types'

export type { UserTranscript } from './useNoraTranscription.types'

const MAX_IN_FLIGHT_UPLOADS = 2
const MAX_QUEUED_UTTERANCES = 4

const fileUri = (filePath: string) =>
  filePath.startsWith('file://') ? filePath : `file://${filePath}`

const wavName = (id: string) => `${id}.wav`

export const useNoraTranscription = (options: UseNoraTranscriptionOptions = {}) => {
  const dispatchAsync = useDispatchAsync()
  const [error, setError] = useState<Error | null>(null)

  const onTranscriptCompleteRef = useRef(options.onTranscriptComplete)
  onTranscriptCompleteRef.current = options.onTranscriptComplete
  const onSpeechStartRef = useRef(options.onSpeechStart)
  onSpeechStartRef.current = options.onSpeechStart
  const onUtteranceReadyRef = useRef(options.onUtteranceReady)
  onUtteranceReadyRef.current = options.onUtteranceReady
  const onErrorRef = useRef(options.onError)
  onErrorRef.current = options.onError
  const onTranscriptChangeRef = useRef(options.onTranscriptChange)
  onTranscriptChangeRef.current = options.onTranscriptChange
  const shouldSuppressCaptureRef = useRef(options.shouldSuppressCapture)
  shouldSuppressCaptureRef.current = options.shouldSuppressCapture
  const onLevelRef = useRef(options.onLevel)
  onLevelRef.current = options.onLevel

  const activeRef = useRef(false)
  const generationRef = useRef(0)
  const queueRef = useRef<NoraUtterance[]>([])
  const inFlightUploadsRef = useRef(0)
  const knownFilePathsRef = useRef(new Set<string>())
  const suppressNextUtteranceRef = useRef(false)

  const deleteFile = useCallback(async (filePath: string) => {
    try {
      await ReactNativeBlobUtil.fs.unlink(filePath)
    } catch {
      // Native stop() may already have removed the cache file.
    }
  }, [])

  const deleteKnownFiles = useCallback(async () => {
    const filePaths = Array.from(knownFilePathsRef.current)
    knownFilePathsRef.current.clear()
    await Promise.all(filePaths.map(deleteFile))
  }, [deleteFile])

  const uploadUtterance = useCallback(
    async (utterance: NoraUtterance, generation: number) => {
      const audio = new ReactNativeFile({
        uri: fileUri(utterance.filePath),
        name: wavName(utterance.id),
        type: 'audio/wav',
        sizeInBytes: utterance.byteCount,
      })

      try {
        const transcript = await dispatchAsync<
          TranscribeUtteranceMutation['transcribeUtterance'] | undefined
        >({
          type: 'nora/transcribeUtterance',
          payload: { id: utterance.id, audio },
        })
        const text = transcript?.text.trim()
        if (generationRef.current !== generation) {
          return
        }

        if (text) {
          const completedTranscript = {
            itemId: transcript?.id || utterance.id,
            text,
          }
          onTranscriptChangeRef.current?.(completedTranscript)
          onTranscriptCompleteRef.current?.(completedTranscript)
        }
      } catch (uploadError) {
        if (generationRef.current === generation) {
          setError(uploadError as Error)
        }
      } finally {
        knownFilePathsRef.current.delete(utterance.filePath)
        await deleteFile(utterance.filePath)
        inFlightUploadsRef.current -= 1
        if (generationRef.current === generation) {
          pumpQueue(generation)
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- pumpQueue is defined below
    [deleteFile, dispatchAsync],
  )

  const pumpQueue = useCallback(
    (generation = generationRef.current) => {
      while (
        activeRef.current &&
        generationRef.current === generation &&
        inFlightUploadsRef.current < MAX_IN_FLIGHT_UPLOADS &&
        queueRef.current.length > 0
      ) {
        const nextUtterance = queueRef.current.shift()
        if (!nextUtterance) {
          return
        }
        inFlightUploadsRef.current += 1
        void uploadUtterance(nextUtterance, generation)
      }
    },
    [uploadUtterance],
  )

  const stop = useCallback(async () => {
    generationRef.current += 1
    activeRef.current = false
    suppressNextUtteranceRef.current = false
    queueRef.current = []
    await NoraAudioCapture.stop()
    await deleteKnownFiles()
  }, [deleteKnownFiles])

  const handleQueueOverflow = useCallback(() => {
    setError(new Error('Transcription queue is full'))
    void stop()
  }, [stop])

  const isCurrentLifecycle = useCallback(
    (generation: number) => activeRef.current && generationRef.current === generation,
    [],
  )
  const hasNewerActiveLifecycle = useCallback(
    (generation: number) => activeRef.current && generationRef.current !== generation,
    [],
  )

  const enqueueUtterance = useCallback(
    (utterance: NoraUtterance) => {
      if (!activeRef.current) {
        void deleteFile(utterance.filePath)
        return
      }

      if (suppressNextUtteranceRef.current || shouldSuppressCaptureRef.current?.()) {
        suppressNextUtteranceRef.current = false
        void deleteFile(utterance.filePath)
        return
      }

      onUtteranceReadyRef.current?.()

      knownFilePathsRef.current.add(utterance.filePath)
      if (inFlightUploadsRef.current < MAX_IN_FLIGHT_UPLOADS) {
        inFlightUploadsRef.current += 1
        void uploadUtterance(utterance, generationRef.current)
        return
      }

      if (queueRef.current.length >= MAX_QUEUED_UTTERANCES) {
        handleQueueOverflow()
        return
      }

      queueRef.current.push(utterance)
    },
    [deleteFile, handleQueueOverflow, uploadUtterance],
  )

  const start = useCallback(async () => {
    setError(null)
    if (activeRef.current) {
      return
    }

    const generation = generationRef.current + 1
    generationRef.current = generation
    activeRef.current = true
    suppressNextUtteranceRef.current = false

    try {
      const permission = await ensureMicrophonePermission()
      if (!permission.granted) {
        throw new Error(
          permission.blocked
            ? 'Microphone permission is blocked'
            : 'Microphone permission was not granted',
        )
      }

      // stop() may have invalidated this startup while permission was being
      // requested. Do not start a native session for an obsolete lifecycle.
      if (!isCurrentLifecycle(generation)) {
        return
      }

      await NoraAudioCapture.start({
        onSpeechStart: () => {
          if (shouldSuppressCaptureRef.current?.()) {
            suppressNextUtteranceRef.current = true
            return
          }

          suppressNextUtteranceRef.current = false
          onSpeechStartRef.current?.()
        },
        onUtterance: enqueueUtterance,
        onLevel: (level) => {
          // While muted the mic keeps running but nothing is sent, so report
          // silence rather than letting the animation react to ignored audio.
          onLevelRef.current?.(shouldSuppressCaptureRef.current?.() ? 0 : level)
        },
        onError: (captureError) => {
          setError(captureError)
          onErrorRef.current?.(captureError)
          void stop()
        },
      })

      // Native startup can resolve after a background/disable transition. Tear
      // down that late session, but never touch a newer generation.
      if (!isCurrentLifecycle(generation)) {
        if (!hasNewerActiveLifecycle(generation)) {
          await NoraAudioCapture.stop()
        }
        return
      }
    } catch (startError) {
      if (generationRef.current !== generation) {
        throw startError
      }
      activeRef.current = false
      setError(startError as Error)
      await deleteKnownFiles()
      throw startError
    }
  }, [deleteKnownFiles, enqueueUtterance, hasNewerActiveLifecycle, isCurrentLifecycle, stop])

  useEffect(
    () => () => {
      void stop()
    },
    [stop],
  )

  return { start, stop, error }
}
